import { hash } from '@ember/helper';
import { service } from '@ember/service';

import { t } from 'ember-intl';

import Component from 'umg-ember-backend/components/record-detail/forms';
import AssetPreview from 'umg-ember-backend/components/ui/asset-preview/index';
import { Codemirror } from 'umg-ember-backend/components/ui/codemirror';
import Form from 'umg-ember-backend/components/ui/form';
import InfoBox from 'umg-ember-backend/components/ui/info-box';
import boolToInt from 'umg-ember-backend/helpers/bool-to-int';

export default class RecordDetailFormsMediaEmbedComponent extends Component {
  // Services
  @service data;

  // Defaults
  embedServices = [];

  // Getter and setter
  get embedVimeoUrlExample() {
    return `https://www.vimeo.com/${
      this.args.model.embedId ? this.args.model.embedId : '<EMBED_ID>'
    }`;
  }

  get embedYoutubeUrlExample() {
    return `https://www.youtube.com/watch?v=${
      this.args.model.embedId ? this.args.model.embedId : '<EMBED_ID>'
    }`;
  }

  get embedYoutubeIframeExample() {
    return `<iframe\n  width="560"\n  height="315"\n  src="https://www.youtube.com/watch?v=${
      this.args.model.embedId ? this.args.model.embedId : '<EMBED_ID>'
    }"\n  frameborder="0"\n  allow="autoplay; encrypted-media"\n  allowfullscreen\n></iframe>`;
  }

  // Constructor
  constructor() {
    super(...arguments);

    this.embedServices = this.data.getCategories({
      'filter[type]': 'MEDIA_EMBED',
      'filter[cmsState]': null,
    });
  }

  // Template
  <template>
    {{! template-lint-disable no-triple-curlies }}
    <Form @model={{@model}} @showErrors={{@showErrors}} as |form|>
      <div class='o-layout'>
        <div class='o-layout__item u-1/1 u-1/2@tablet'>
          <form.fieldset @titleKey='general' as |fieldset|>
            <fieldset.field
              @property='cmsState'
              @type='select-value'
              @options={{this.cmsStateOptions}}
            />

            <fieldset.field
              @property='cmsLanguageId'
              @type='select-value'
              @options={{this.availableLanguages}}
            />

            <fieldset.field @property='headline' />

            <fieldset.field
              @filters={{hash assetFolderId='2'}}
              @property='backgroundImage'
              @options={{hash allowedFolder='100'}}
              @type='belongs-to'
              as |record|
            >
              <AssetPreview @asset={{record}} />
            </fieldset.field>

            <fieldset.field @property='backgroundColor' @type='color' />
          </form.fieldset>
        </div>
        <div class='o-layout__item u-1/1 u-1/2@tablet'>
          <form.fieldset @titleKey='assignments' as |fieldset|>
            <fieldset.field
              @property='channels'
              @type='has-many'
              @on={{hash openNewRecord=false}}
            />
          </form.fieldset>

          <form.fieldset @titleKey='embed' as |fieldset|>
            <fieldset.field
              @property='embedService'
              @type='select-record'
              @isSearchEnabled={{true}}
              @options={{this.embedServices}}
            />

            {{#if @model.isFacebook}}
              <fieldset.field
                @property='embedOptions.facebookCode'
                @type='code-editor'
                @codeFormat='html'
              />

              {{#if @model.embedOptions.facebookCode}}
                {{{@model.embedOptions.facebookCode}}}
              {{/if}}
            {{else if @model.isInstagram}}
              <fieldset.field
                @property='embedOptions.instagramCode'
                @type='code-editor'
                @codeFormat='html'
              />

              {{#if @model.embedOptions.instagramCode}}
                <InfoBox @type='warning'>
                  {{t 'record_detail.forms.media_embed.embedInstagramWarning'}}
                </InfoBox>
                {{{@model.embedOptions.instagramCode}}}
              {{/if}}
            {{else if @model.isSoundcloud}}
              <fieldset.field
                @property='embedOptions.soundcloudCode'
                @type='code-editor'
                @codeFormat='html'
              />

              {{#if @model.embedOptions.soundcloudCode}}
                {{{@model.embedOptions.soundcloudCode}}}
              {{/if}}
            {{else if @model.isTwitter}}
              <fieldset.field
                @property='embedOptions.twitterCode'
                @type='code-editor'
                @codeFormat='html'
              />

              {{#if @model.embedOptions.twitterCode}}
                {{{@model.embedOptions.twitterCode}}}
              {{/if}}
            {{else if @model.isAppleMusic}}
              <fieldset.field
                @property='embedOptions.appleMusicCode'
                @type='code-editor'
                @codeFormat='html'
              />

              {{#if @model.embedOptions.appleMusicCode}}
                {{{@model.embedOptions.appleMusicCode}}}
              {{/if}}
            {{else if @model.isSpotify}}
              <fieldset.field
                @property='embedOptions.spotifyCode'
                @type='code-editor'
                @codeFormat='html'
              />

              {{#if @model.embedOptions.spotifyCode}}
                {{{@model.embedOptions.spotifyCode}}}
              {{/if}}
            {{else if @model.isTiktok}}
              <fieldset.field
                @property='embedOptions.tiktokCode'
                @type='code-editor'
                @codeFormat='html'
              />

              {{#if @model.embedOptions.tiktokCode}}
                {{{@model.embedOptions.tiktokCode}}}
              {{/if}}
            {{else if @model.isVimeo}}
              <fieldset.field @property='embedId' />

              <label>
                {{t 'record_detail.forms.media_embed.embedExample'}}
              </label>

              <Codemirror
                @value={{this.embedVimeoUrlExample}}
                @readOnly={{true}}
                @mode='html'
              />

              {{#if @model.embedId}}
                {{! template-lint-disable no-inline-styles }}
                <iframe
                  src='https://player.vimeo.com/video/{{@model.embedId}}?autoplay={{boolToInt
                    @model.embedOptions.vimeoAutoplay
                  }}&loop={{boolToInt
                    @model.embedOptions.vimeoLoop
                  }}&title={{boolToInt
                    @model.embedOptions.vimeoTitle
                  }}&byline={{boolToInt
                    @model.embedOptions.vimeoByline
                  }}&portrait={{boolToInt @model.embedOptions.vimeoPortrait}}'
                  style='width:100%;height:100%;'
                  title={{t
                    'record_detail.forms.media_embed.vimeo_embed_title'
                  }}
                  frameborder='0'
                  webkitallowfullscreen
                  mozallowfullscreen
                  allowfullscreen
                ></iframe>
              {{/if}}

              <fieldset.field
                @property='embedOptions.vimeoAutoplay'
                @type='checkbox'
              />
              <fieldset.field
                @property='embedOptions.vimeoLoop'
                @type='checkbox'
              />
              <fieldset.field
                @property='embedOptions.vimeoTitle'
                @type='checkbox'
              />
              <fieldset.field
                @property='embedOptions.vimeoByline'
                @type='checkbox'
              />
              <fieldset.field
                @property='embedOptions.vimeoPortrait'
                @type='checkbox'
              />
            {{else if @model.isYoutube}}
              <fieldset.field @property='embedId' />

              <label class='form-label'>
                {{t 'record_detail.forms.media_embed.embedExample'}}
              </label>

              <Codemirror
                @value={{this.embedYoutubeUrlExample}}
                @readOnly={{true}}
                @mode='html'
              />

              <Codemirror
                @value={{this.embedYoutubeIframeExample}}
                @readOnly={{true}}
                @mode='html'
              />

              {{#if @model.embedId}}
                <iframe
                  src='https://www.youtube.com/embed/{{@model.embedId}}?rel={{boolToInt
                    @model.embedOptions.youtubeSuggestVideos
                  }}&controls={{boolToInt
                    @model.embedOptions.youtubeShowControls
                  }}&showinfo={{boolToInt
                    @model.embedOptions.youtubeShowInfo
                  }}&start={{@model.embedOptions.youtubeStartAt}}&{{@model.embedOptions.youtubeDataProtection}}'
                  width='100%'
                  height='400px'
                  frameborder='0'
                  allow='autoplay; encrypted-media'
                  title={{t
                    'record_detail.forms.media_embed.youtube_embed_title'
                  }}
                  allowfullscreen
                ></iframe>
                <br />
              {{/if}}

              <fieldset.field
                @property='embedOptions.youtubeSuggestVideos'
                @type='checkbox'
              />
              <fieldset.field
                @property='embedOptions.youtubeShowControls'
                @type='checkbox'
              />
              <fieldset.field
                @property='embedOptions.youtubeShowInfo'
                @type='checkbox'
              />
              <fieldset.field
                @property='embedOptions.youtubeDataProtection'
                @type='checkbox'
              />
              <fieldset.field
                @property='embedOptions.youtubeStartAt'
                @type='number'
              />
            {{/if}}
          </form.fieldset>

          <form.sections.meta />
        </div>
      </div>
    </Form>
  </template>
}
